<template lang="pug">
//- An OAuth handshake parks the browser on THIS page with nothing for the user
//- to do: the MCP round-trip uses /login as its redirect_uri, so a logged-in
//- visitor lands here twice (once as ?oauth=platform..., once as ?code&state)
//- on the way to the page they asked for. Showing the sign-in form during that
//- window reads as "you got logged out" and invites a pointless login attempt,
//- so the form is withheld behind a loader until we know the user really has to
//- sign in. authBusy is decided synchronously in setup, so the form never
//- paints first. Every path that ends in "the user must sign in" clears it.
.bq-login(v-if="authBusy")
    .bq-waiting
        BunnyLoader(:overlay="false" :label="authBusyLabel")

.bq-login(v-else)
    //- router-link.bq-logo(to="/") ₍ᐢ•⩊•ᐢ₎
    .bq-signup
        router-link(to="/")
            SkapiBunnyLockup
        br
        br
        span You can use your Skapi account email to login.

    h1.bq-title Login

    form.bq-form(@submit.prevent="login")
        label.bq-label
            span Email
            input(type="email" autocomplete="email" @input="e=> { form.email = e.target.value; error='';}" :disabled="promiseRunning" placeholder="your@email.com" required :value="form.email")

        label.bq-label
            span Password
            input(type='password'
                @input="(e)=>{form.password = e.target.value; error='';}" 
                :disabled="promiseRunning"
                autocomplete="current-password"
                name="password" placeholder="Enter password" required)

        .bq-actions
            Checkbox(style='font-weight:unset;' @change="(e)=>{setLocalStorage(e)}" :disabled='promiseRunning' v-model='rememberMe') Remember Me
            RouterLink.bq-link.bq-cta-link(to="/forgot-password" data-track="login_forgotPw" :class='{disabled: promiseRunning}') Forgot Password?

        .bq-error(v-if="openIdLoginError || error")
            template(v-if="enableAccount")
                | {{ error }}
            span(v-else) {{ openIdLoginError || error }}

        .bq-error(v-if="enableAccount")
            router-link.bq-link(:to='"/enable-account/" + form.email') [Click Here]
            span &nbsp;to enable your account.

        .bq-bottom
            button.bq-btn(type="submit" data-track="login_login" :class="{ 'bq-btn--loading': promiseRunning }")
                span.bq-loader(v-if="promiseRunning")
                template(v-else) Login

        //- Sign-up sits directly under the Login button. Someone who has no
        //- account is looking at the button they cannot use, not at the header
        //- above the form, so the way out has to be in that line of sight.
        .bq-signup-cta
            span No account?&nbsp;
            RouterLink.bq-cta-link(data-track="login_signUp" :to="{ name: 'signup' }" :class='{disabled: promiseRunning}')
                | Sign up
                span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

    .bq-divider
        .bq-divider-line
        span.bq-divider-text OR
        .bq-divider-line

    button.bq-google(@click="e=>googleLogin(e,'login_googleLogin', route.query.oauth === 'platform')" :disabled="promiseRunning" data-track="login_googleLogin")
        svg(viewBox="0 0 48 48" style="width:20px;height:20px;flex:none;")
            path(fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z")
            path(fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z")
            path(fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z")
            path(fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z")
            path(fill="none" d="M0 0h48v48H0z")
        span Continue with Google

Modal(:open="enableAccount")
    .bq-modal
        h2.bq-modal-title Enable Account
        p.bq-modal-desc Your account is disabled. Please contact support to enable your account. You can also try resetting your password.
        .bq-modal-btns
            button.bq-btn--outline(type="button" @click="enableAccount = false") Cancel
            button(type="button" data-track="login_enableAccount" @click="router.push('/enable-account/' + form.email)") Enable
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { skapi, onLoginHanger } from "@/main";
import { SkapiError } from "skapi-js";
import { genOAuthCallbackUrl, handleGoogleOAuthReturn, isGoogleOAuthReturn, openIdLoginError } from "@/oauth";
import { user } from "@/code/user";
import { onMounted, ref } from "vue";
import { googleLogin } from "@/oauth";
import SkapiBunnyLockup from "@/components/skapiBunnyLockup.vue";
import BunnyLoader from "@/components/bunnyLoader.vue";
import {
    beginMcpOAuthOnLogin,
    completeMcpAuthorize,
    isMcpOAuthCallback,
} from "@/code/mcp_oauth";

import Checkbox from "@/components/checkbox.vue";
import Modal from "@/components/modal.vue";
import { removeFullScreenLoader } from "@/components/jsComponents";

const router = useRouter();
const route = useRoute();

// Is this visit a hand-off rather than a sign-in? Decided from the URL and the
// existing session ONLY, before first paint, so a real sign-in never sees the
// loader and a hand-off never sees the form. Each case is one the app resolves
// by itself, without the user touching anything:
//   - ?code&state matching our stored MCP state -> completing the token
//     exchange, then routing to the page the user originally asked for.
//   - ?oauth=platform&redirect_uri&state -> an external app (the MCP server)
//     sent the user here to authenticate. A logged-in user bounces straight
//     back; a logged-OUT user must sign in, so the hanger below clears authBusy.
//   - a Google round-trip coming back with ?code.
//   - already signed in with no OAuth params -> the hanger pushes /my-projects.
// Anything else (the ordinary "I want to log in" visit) leaves authBusy false.
const initialQuery = route.query || {};
const isMcpReturn = isMcpOAuthCallback(initialQuery as any);
const isPlatformInbound =
    initialQuery.oauth === "platform" && !!initialQuery.state && !!initialQuery.redirect_uri;
const isGoogleReturn = isGoogleOAuthReturn();
const isAlreadySignedIn = !!user?.user_id || !!skapi.session?.accessToken?.jwtToken;

const authBusy = ref(isMcpReturn || isPlatformInbound || isGoogleReturn || isAlreadySignedIn);
const authBusyLabel = ref(
    isGoogleReturn
        ? "Signing you in with Google"
        : isPlatformInbound
          ? "Connecting your account"
          : isMcpReturn
            ? "Setting up your AI session"
            : "Signing you in",
);

// Reveal the sign-in form: the flow finished somewhere that genuinely needs the
// user. Paired with every removeFullScreenLoader() call site, which marks
// exactly those moments for the app-wide loader.
const showLoginForm = () => {
    authBusy.value = false;
};

let rememberMe = ref(window.localStorage.getItem("remember_me") !== "false"); // dom 업데이트시 checkbox value 유지하기 위함
let promiseRunning = ref(false);
let error = ref(null);
let form = {
    email: String(route.query.e || ""),
    password: "",
};

let enableAccount = ref(false);

onLoginHanger["login.vue"] = async (u) => {
    // If we're returning from MCP /oauth/authorize, complete the token
    // exchange first and bounce to the originally-intended page. This runs
    // before the my-projects redirect so the ?code/state querystring isn't
    // mistaken for an unrelated OAuth flow.
    const q = route.query || {};
    if (isMcpOAuthCallback(q as any)) {
        try {
            const { redirectAfter } = await completeMcpAuthorize(q as any);
            router.push(redirectAfter || "/my-projects");
            return;
        } catch (err) {
            console.error("MCP OAuth token exchange failed", err);
            error.value = "Failed to complete MCP authorization.";
            removeFullScreenLoader();
            showLoginForm();
            return;
        }
    }

    // INBOUND: external app (e.g. ChatGPT via the skapi-mcp server) sent the
    // user here with ?oauth=platform&redirect_uri=...&state=... to authenticate.
    // Stash params so any auth path (form login, Google round-trip) can
    // recover them, then if the user is already logged in bounce straight
    // back to the caller's redirect_uri with a session-derived code.
    if (q.oauth === "platform" && q.state && q.redirect_uri) {
        try {
            window.sessionStorage.setItem(
                "oauth:" + String(q.state),
                JSON.stringify(q),
            );
        } catch {}

        if (u?.user_id) {
            await returnOAuthToMCP(q);
            return;
        }
    }

    let bound = await handleGoogleOAuthReturn();

    if(bound !== 'redirecting') {
        if(u?.user_id) {
            // Don't navigate away if an inbound oauth=platform flow is still
            // waiting (handled above when redirect_uri is present).
            if (q.oauth === "platform" && q.state && q.redirect_uri) {
                removeFullScreenLoader();
                showLoginForm();
                return;
            }
            router.push("/my-projects");
        }
        else {
            removeFullScreenLoader();
            showLoginForm();
        }
    }
};

onMounted(() => {
    if(onLoginHanger["login.vue"]) {
        onLoginHanger["login.vue"](user);
        delete onLoginHanger["login.vue"];
    }
});

let setLocalStorage = (e:Event) => {
    const target = e.target as HTMLInputElement;
    localStorage.setItem("remember_me", target.checked ? "true" : "false");
};

let returnOAuthToMCP = async (query: any) => {
    if (query.oauth !== "platform" || !query.state) return;

    // Reached either straight from the hanger (already signed in) or right
    // after a form/Google sign-in. Either way the browser is about to leave for
    // the calling platform, so cover the form for the second case too.
    authBusy.value = true;
    authBusyLabel.value = "Connecting your account";

    // Race-guard: on refresh, onLogin can fire with a populated user_id
    // before skapi.session is wired up. Poll briefly so we don't throw
    // (and silently leave the loader spinning) inside genOAuthCallbackUrl.
    let session = skapi.session;
    let waited = 0;
    while (!session?.accessToken?.jwtToken && waited < 3000) {
        await new Promise((r) => setTimeout(r, 100));
        session = skapi.session;
        waited += 100;
    }

    if (!session?.accessToken?.jwtToken) {
        console.error("OAuth bounce aborted: no skapi session.");
        removeFullScreenLoader();
        showLoginForm();
        return;
    }

    // retrieve stored oauth params (including redirect_uri)
    let storedParams = null;
    let stored = window.sessionStorage.getItem("oauth:" + query.state);
    if (stored) {
        try {
            storedParams = JSON.parse(stored);
        } catch (e) {
            // ignore parse error
        }
        window.sessionStorage.removeItem("oauth:" + query.state);
    }

    const callbackUrl = genOAuthCallbackUrl(
        query.state as string,
        session,
        storedParams || query
    );

    // Refused: the redirect_uri on this link does not belong to the MCP
    // server, so there is nowhere safe to hand the session. Do NOT navigate.
    // An empty href would reload this page and spin. Show the form instead so
    // the visitor is told rather than silently parked on a loader.
    if (!callbackUrl) {
        error.value = "This sign-in link isn't valid. Please start again from the app you were connecting.";
        removeFullScreenLoader();
        showLoginForm();
        return;
    }

    window.location.href = callbackUrl;
};

let login = (e: Event) => {
    if (promiseRunning.value) return;
    promiseRunning.value = true;

    let params = {
        email: form.email,
        password: form.password,
    };

    skapi
        .login(params)
        .then(async (u) => {
            let routerQuery = route.query || {};

            if(routerQuery.oauth === "platform") {
                returnOAuthToMCP(routerQuery);
                promiseRunning.value = false;
                return;
            }

            // Register a fresh MCP OAuth client and start the authorization
            // code flow. This redirects the browser away to the MCP server;
            // when it comes back to /login the onLoginHanger above
            // exchanges the code for tokens and routes to /my-projects.
            try {
                await beginMcpOAuthOnLogin("/my-projects");
                return; // browser is leaving the SPA
            } catch (err) {
                console.error("MCP OAuth bootstrap failed", err);
                // Fall through to the normal post-login redirect so the user
                // isn't stranded on the login screen if MCP is down.
            }

            router.push("/my-projects").then(() => {
                promiseRunning.value = false;
            });
        })
        .catch((err) => {
            if (!(err instanceof SkapiError)) throw err;

            if (err.code === "SIGNUP_CONFIRMATION_NEEDED") {
                router.push({
                    path: "/signup-confirmation",
                    query: { email: form.email },
                });
            }
            else if (err.code === "USER_IS_DISABLED") {
                error.value = "This account is disabled.";
                enableAccount.value = true;
            }
            else if (err.code === "INCORRECT_USERNAME_OR_PASSWORD") {
                error.value = "Incorrect email or password.";
            }
            else if (err.code === "NOT_EXISTS") {
                error.value = "Incorrect email or password.";
            }
            else if (err.message.includes("NOT_EXISTS")) {
                error.value = "The account does not exist.";
            }
            else {
                error.value = err.message;
            }

            promiseRunning.value = false;
        });
};

</script>

<style scoped lang="less">
@import "@/assets/less/bq-tokens.less";

.bq-login {
    max-width: 440px;
    padding: 3rem 1.5rem;
    margin: 0 auto;
    font-family: @mono;
    color: @ink;
}

/* ─── oauth hand-off ─── */
.bq-waiting {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
}

.bq-logo {
    font-size: 1.2rem;
    color: @ink;
    text-decoration: none;
    letter-spacing: 0.04em;

    &:hover {
        text-decoration: none;
    }
}

.bq-title {
    font-size: 1.6rem;
    font-weight: 700;
    margin: 2rem 0 1.5rem;
    letter-spacing: -0.01em;
}

/* ─── form ─── */
.bq-form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.bq-label {
    display: flex;
    flex-direction: column;
    // gap: 0.4rem;
    font-size: 0.82rem;
    font-weight: 600;
    letter-spacing: 0.04em;
}

.bq-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    font-size: 0.82rem;
}

.bq-link {
    font-size: 0.82rem;
}

/* ─── error ─── */
.bq-error {
    padding: 0.75rem 1rem;
    border: 1px solid @danger;
    background: @danger-bg;
    color: @danger;
    font-size: 0.82rem;
    line-height: 1.5;
}

/* ─── bottom ─── */
.bq-bottom {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin-top: 0.5rem;
    text-align: center;
}

.bq-signup {
    font-size: 0.82rem;
    color: @muted;
}

/* ─── sign-up call to action (directly under the Login button) ─── */
.bq-signup-cta {
    margin-top: 1.5rem;
    text-align: center;
    font-size: 0.82rem;
    color: @muted;
}

/* The link itself is the shared .bq-cta-link from bq-shared.less. */

/* ─── divider ─── */
.bq-divider {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin: 2rem 0;
}

.bq-divider-line {
    flex: 1;
    height: 1px;
    background: @line;
}

.bq-divider-text {
    font-size: 0.72rem;
    color: @muted;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

/* ─── google button ─── */
.bq-google {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    width: 100%;
    max-width: none;
    height: auto;
    min-height: 0;
    padding: 0.75rem 1rem;
    border: 1px solid @line;
    background: @paper;
    font-family: @mono;
    font-size: 0.88rem;
    color: @ink;
    cursor: pointer;
    border-radius: 0;
    box-sizing: border-box;
    transition: border-color 0.15s, transform 0.12s ease, box-shadow 0.12s ease;

    &::after {
        display: none;
    }

    &:hover {
        border-color: @ink;
    }

    &:disabled {
        opacity: 0.3;
        pointer-events: none;
    }
}

/* ─── modal ─── */
.bq-modal {
    padding: 2rem;
    font-family: @mono;
}

.bq-modal-title {
    font-size: 1.1rem;
    font-weight: 700;
    margin-bottom: 1rem;
}

.bq-modal-desc {
    font-size: 0.88rem;
    color: @muted;
    line-height: 1.6;
    margin-bottom: 1.5rem;
}

.bq-modal-btns {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
}

/* ─── mobile ─── */
@media (max-width: 480px) {
    .bq-login {
        padding: 2rem 1.25rem;
    }

    .bq-bottom {
        flex-direction: column-reverse;
        align-items: stretch;
        text-align: center;
    }
}
</style>
